import MUIPopover, {
    PopoverClasses,
    PopoverOrigin,
    PopoverPosition,
    PopoverReference,
} from "@mui/material/Popover";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInsideClick, useOutsideClick } from "utils/utils";
import "./Popover.scss";

export interface PopoverProps {
    trigger?: ReactNode;
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
    closeOnInsideClick?: boolean;
    closeOnOutsideClick?: boolean;
    anchorReference?: PopoverReference;
    anchorPosition?: PopoverPosition;
    isOpen?: boolean;
    classes?: Partial<PopoverClasses>;
    toggleClassName?: string;
    contentClassName?: string;
    openOnAction?: "click" | "hover";
    rootClassName?: string;
    closeHandler?: () => void;
    onBlur?: (e) => void;
    actionOnOpen?: (event?: any) => void;
    handleScroll?: (e) => void;
    closeOnEnter?: boolean;
    // When set, keep the CSS arrow/tip of the popover content pointing at this
    // element (resolved WITHIN the trigger) instead of a fixed offset. MUI shifts
    // the paper horizontally to keep it in-viewport, so a static `left` on the
    // arrow drifts off the trigger; this measures the real rendered rects and sets
    // a `--popover-arrow-left` CSS variable on the content div so the arrow tracks
    // the target's horizontal centre. Opt-in — unset = legacy fixed-offset arrow.
    arrowTargetSelector?: string;
}

export const popoverPosition: Partial<PopoverProps> = {
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: {
        vertical: -5,
        horizontal: "left",
    },
};

export const centeredPopoverRootStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2000,
};

export const Popover = ({
    children,
    anchorOrigin,
    transformOrigin,
    trigger,
    anchorPosition,
    isOpen,
    closeHandler,
    classes,
    toggleClassName,
    contentClassName,
    openOnAction = "click",
    closeOnInsideClick = true,
    closeOnOutsideClick = true,
    anchorReference = "anchorEl",
    rootClassName,
    onBlur,
    actionOnOpen,
    handleScroll,
    closeOnEnter,
    arrowTargetSelector,
}: React.PropsWithChildren<PopoverProps>) => {
    const popoverRef = useRef() as any;
    const anchorEl = useRef(null);
    const [internalIsOpened, setInternalIsOpened] = useState(false);

    const onMouseClick = useCallback(
        (event: any) => {
            if (openOnAction === "click") {
                setInternalIsOpened(true);
                actionOnOpen?.(event);
            }
        },
        [actionOnOpen, openOnAction]
    );

    const handleClose = () => {
        setInternalIsOpened(false);
    };

    const onMouseEnter = useCallback(
        (event: any) => {
            if (openOnAction === "hover") {
                setInternalIsOpened(true);
            }
        },
        [openOnAction]
    );

    const onMouseLeave = useCallback(() => {
        if (openOnAction === "hover") {
            setInternalIsOpened(false);
        }
    }, [openOnAction]);

    // A controlled popover (`isOpen` passed) can be closed by its parent, e.g. a
    // Cancel button inside the content, without going through handleClose. The
    // expanded class therefore follows the effective open state, so the trigger
    // does not keep looking "open" after the parent closes it.
    const isExpanded = isOpen ?? internalIsOpened;

    useEffect(() => {
        if (isOpen === false) {
            setInternalIsOpened(false);
        }
    }, [isOpen]);

    const toggleClasses = useMemo(
        () => `popover__toggle ${toggleClassName ?? ""} ${isExpanded ? "popover--expanded" : ""}`,
        [isExpanded, toggleClassName]
    );

    const triggerComponent = useMemo(
        () =>
            trigger ? (
                <span
                    ref={anchorEl}
                    className={toggleClasses}
                    onClick={onMouseClick}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    aria-haspopup="true"
                >
                    {trigger}
                </span>
            ) : null,
        [trigger, toggleClasses, onMouseClick, onMouseEnter, onMouseLeave]
    );

    useInsideClick(popoverRef, () => {
        if (closeOnInsideClick) {
            closeHandler?.();
            handleClose();
        }
    });

    useOutsideClick(popoverRef, () => {
        if (closeOnOutsideClick) {
            closeHandler?.();
        }
    });

    // Keep the arrow/tip pointing at `arrowTargetSelector` (within the trigger) by
    // measuring the ACTUAL rendered paper + target rects — robust to MUI's
    // in-viewport horizontal shift, which a fixed CSS offset cannot follow. Sets a
    // `--popover-arrow-left` CSS variable the content's `::before` arrow reads.
    const positionArrow = useCallback(() => {
        if (!arrowTargetSelector) return;
        const content = popoverRef.current as HTMLElement | undefined;
        const anchor = anchorEl.current as HTMLElement | null;
        if (!content || !anchor) return;
        const paper = content.closest(".MuiPopover-paper") as HTMLElement | null;
        if (!paper) return;
        const target =
            (anchor.querySelector(arrowTargetSelector) as HTMLElement | null) ?? anchor;
        const targetRect = target.getBoundingClientRect();
        const paperRect = paper.getBoundingClientRect();
        const halfArrowBase = 7.5; // border-width: … 7.5px … → apex sits 7.5px right of `left`
        const targetCentre = targetRect.left + targetRect.width / 2;
        let left = targetCentre - paperRect.left - halfArrowBase;
        // Never let the arrow leave the paper (keeps it a sensible tip even when the
        // target is far outside a heavily-shifted paper).
        const edge = 6;
        left = Math.max(edge, Math.min(paperRect.width - edge - halfArrowBase * 2, left));
        content.style.setProperty("--popover-arrow-left", `${Math.round(left)}px`);
    }, [arrowTargetSelector]);

    useEffect(() => {
        const opened = isOpen ?? internalIsOpened;
        if (!opened || !arrowTargetSelector) return undefined;
        const raf = requestAnimationFrame(positionArrow);
        const handler = () => positionArrow();
        window.addEventListener("scroll", handler, true);
        window.addEventListener("resize", handler);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("scroll", handler, true);
            window.removeEventListener("resize", handler);
        };
    }, [isOpen, internalIsOpened, arrowTargetSelector, positionArrow]);

    return (
        <>
            {triggerComponent}
            <MUIPopover
                anchorReference={anchorReference}
                anchorPosition={anchorPosition}
                anchorEl={anchorEl.current}
                onClose={(e) => {
                    handleClose();
                    onBlur?.(e);
                }}
                open={isOpen ?? internalIsOpened}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                classes={classes}
                disableRestoreFocus
                PaperProps={{ onMouseEnter, onMouseLeave }}
                TransitionProps={{ onEntered: positionArrow }}
                className={rootClassName}
            >
                <div
                    className={contentClassName}
                    ref={popoverRef}
                    onScroll={handleScroll}
                    onKeyDown={(e) => {
                        if (closeOnEnter && e.key === "Enter") {
                            e.preventDefault();
                            handleClose();
                        }
                    }}
                >
                    {children}
                </div>
            </MUIPopover>
        </>
    );
};
